import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import useMyFetch from "../useMyFetch";
import handleError from "@composables/handleError";
import { useToastsStore } from "@/stores/toastsStore";
import { get } from "@utils/toolBox";
import { captureEvent } from "@/lib/analytics";

export const useDefaultMessagesStore = defineStore("defaultMessages", () => {
  const toasts = useToastsStore();
  const serverErrorDetail = (error) => [error ?? []].flat().join(", ") || null;
  // state
  const allMessages = ref([]);
  const selectedLanguage = ref(null);
  const editedMessages = ref({});
  const fetchedLanguage = ref(null);

  // computed
  const selectedMessage = computed(() => allMessages.value.find((dm) => dm.language === selectedLanguage.value));
  const englishMessage = computed(() => allMessages.value.find((dm) => dm.language === "en"));
  const defaultMessages = computed(() => {
    // When used by survey (single language fetch), use fetchedLanguage
    // When used by admin (fetchAll), use selectedLanguage
    const lang = fetchedLanguage.value || selectedLanguage.value;
    const dm = allMessages.value.find((m) => m.language === lang);
    return dm?.default_messages || {};
  });

  watch(selectedMessage, (dm) => {
    editedMessages.value = { ...(dm?.default_messages || {}) };
  });

  // actions — single language (survey usage)
  const fetch = async (language = "en") => {
    if (fetchedLanguage.value === language) return;
    const { data } = await useMyFetch(`default_messages/${language}`).json();
    const dm = data.value;
    if (dm) {
      const index = allMessages.value.findIndex((m) => m.language === dm.language);
      if (index !== -1) {
        allMessages.value[index] = dm;
      } else {
        allMessages.value.push(dm);
      }
    }
    fetchedLanguage.value = language;
  };

  const getSettingMessage = ({ pathInfo, cardType }) => {
    const computedPath = pathInfo?.type ? `${pathInfo.path}.${cardType}` : pathInfo.path;
    return get(defaultMessages.value, computedPath, "");
  };

  const ensureLanguages = async (languages) => {
    const missing = [...new Set(languages)].filter(
      (language) => language && !allMessages.value.some((m) => m.language === language),
    );
    await Promise.all(
      missing.map(async (language) => {
        const { data } = await useMyFetch(`default_messages/${language}`).json();
        if (data.value) allMessages.value.push(data.value);
      }),
    );
  };

  // actions — all languages (admin usage)
  const fetchAll = async () => {
    try {
      const { response, data } = await useMyFetch("default_messages").json().get();
      if (response.value.ok) {
        allMessages.value = data.value;
        if (!selectedLanguage.value && data.value.length > 0) {
          selectedLanguage.value = data.value[0].language;
        }
      }
    } catch (err) {
      handleError(err);
    }
  };

  const createMessage = async (language) => {
    const emptyMessages = {
      skip_text: "",
      error_message: "",
      screenout_hard: "",
      quota_hard: "",
      inactive_survey: "",
      start_button: "",
      start_text: "",
      start_consent: "",
      reward_button: "",
      reward_decline: "",
      reward_subtitle: "",
      reward_choice: "",
      reward_delay: "",
      reward_email_placeholder: "",
      reward_email_notice: "",
      reward_submit: "",
      reward_edit: "",
      reward_close: "",
    };
    const { response, data, error } = await useMyFetch("default_messages")
      .json()
      .post({ default_message: { language, default_messages: emptyMessages } });
    if (response.value.ok) {
      allMessages.value.push(data.value);
      selectedLanguage.value = data.value.language;
      captureEvent("default_messages_language_added", { language });
    } else {
      toasts.error("Failed to add language", serverErrorDetail(error.value));
    }
    return response.value.ok;
  };

  const updateMessage = async () => {
    const dm = selectedMessage.value;
    if (!dm) return false;

    const { response, data, error } = await useMyFetch(`default_messages/${dm.language}`)
      .json()
      .patch({ default_message: { default_messages: editedMessages.value } });
    if (response.value.ok) {
      const index = allMessages.value.findIndex((m) => m.language === dm.language);
      if (index !== -1) allMessages.value[index] = data.value;
      captureEvent("default_messages_saved", { language: dm.language });
    } else {
      toasts.error("Failed to save default messages", serverErrorDetail(error.value));
    }
    return response.value.ok;
  };

  const deleteMessage = async (language) => {
    try {
      if (!language || language === "en") return false;

      const { response } = await useMyFetch(`default_messages/${language}`).json().delete();
      if (response.value.ok) {
        captureEvent("default_messages_language_deleted", { language });

        const index = allMessages.value.findIndex((m) => m.language === language);
        if (index !== -1) allMessages.value.splice(index, 1);
        if (selectedLanguage.value === language) {
          selectedLanguage.value = allMessages.value[0]?.language || null;
        }
      }
      return response.value.ok;
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  const selectLanguage = (language) => {
    selectedLanguage.value = language;
  };

  const isLanguageSelected = (language) => {
    return language === selectedLanguage.value;
  };

  return {
    allMessages,
    defaultMessages,
    selectedLanguage,
    selectedMessage,
    englishMessage,
    editedMessages,
    fetch,
    fetchAll,
    getSettingMessage,
    ensureLanguages,
    createMessage,
    updateMessage,
    deleteMessage,
    selectLanguage,
    isLanguageSelected,
  };
});
